import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import "bootstrap/dist/css/bootstrap.min.css";
import "../assets/css/style.css";

import api, { IMAGE_BASE_URL } from "../api/Api.js";
import { useLanguage } from "../context/LanguageContext";
import tickImage from "../assets/images/tickm.png";
const OurCars = () => {
  const navigate = useNavigate();
  const { language, t } = useLanguage();

  // ============================================================
  // TRANSLATION HELPER
  // ============================================================

  const tt = (key, fallback = "") => {
    return t(`ourcars.${key}`, fallback);
  };

  // ============================================================
  // STATE
  // ============================================================

  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);

  // IMPORTANT:
  // Stores which cars are expanded
  // Example:
  // {
  //   62: true,
  //   63: false
  // }
  const [expandedCars, setExpandedCars] = useState({});

  // ============================================================
  // CAR DETAILS MODAL
  // ============================================================

  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedCar, setSelectedCar] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState("");

  // ============================================================
  // LANGUAGE ID
  // ============================================================

  const getLanguageId = () => {
    if (!language) {
      return 1;
    }

    if (typeof language === "object") {
      return (
        Number(
          language.lang_id ||
            language.language_id ||
            language.id
        ) || 1
      );
    }

    return Number(language) || 1;
  };

  const languageId = getLanguageId();

  // ============================================================
  // IMAGE
  // ============================================================

  const getCarImage = (image) => {
    if (!image) {
      return "/images/car_2.jpg";
    }

    const imagePath = String(image).trim();

    if (!imagePath) {
      return "/images/car_2.jpg";
    }

    // Already complete URL
    if (
      imagePath.startsWith("http://") ||
      imagePath.startsWith("https://")
    ) {
      return imagePath;
    }

    const imageBase =
      IMAGE_BASE_URL || "http://localhost:5555";

    // Remove leading slash
    const cleanPath = imagePath.replace(/^\/+/, "");

    return `${imageBase}/${cleanPath}`;
  };

  // ============================================================
  // IMAGE ERROR / FALLBACK
  // ============================================================

  const handleImageError = (e) => {
    if (e.currentTarget.dataset.fallback === "true") {
      return;
    }

    e.currentTarget.dataset.fallback = "true";
    e.currentTarget.src = "/images/car_2.jpg";
  };

  // ============================================================
  // FETCH CAR LIST
  // ============================================================

  useEffect(() => {
    let cancelled = false;

    const fetchCars = async () => {
      try {
        setLoading(true);

        const response = await api.get(
          `/cars/getCars?lang_id=${languageId}`
        );

        if (cancelled) {
          return;
        }

        if (response.data?.success) {
          const carList = Array.isArray(
            response.data.data
          )
            ? response.data.data
            : [];

          console.log("🚗 CAR LIST:", carList);

          if (carList.length > 0) {
            console.log(
              "🚗 FIRST CAR:",
              carList[0]
            );

            console.log(
              "🚗 FIRST CAR FEATURES:",
              carList[0].features
            );

            console.log(
              "🚗 FIRST CAR INCLUDED ITEMS:",
              carList[0].includedItems
            );
          }

          setCars(carList);

          // Reset expanded state after language change
          setExpandedCars({});
        } else {
          setCars([]);
        }
      } catch (error) {
        if (!cancelled) {
          console.error(
            "CAR LIST ERROR:",
            error
          );

          setCars([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchCars();

    return () => {
      cancelled = true;
    };
  }, [languageId]);

  // ============================================================
  // BOOK CAR
  // ============================================================

  const handleBookCar = (car) => {
    const existingCriteria =
      sessionStorage.getItem("bookingCriteria");

    if (existingCriteria) {
      try {
        const criteria =
          JSON.parse(existingCriteria);

        criteria.selectedCarId =
          car.carmodel_id ||
          car.car_id ||
          car.id;

        criteria.selectedCar = car;

        sessionStorage.setItem(
          "bookingCriteria",
          JSON.stringify(criteria)
        );
      } catch (error) {
        console.error(
          "Unable to update booking criteria:",
          error
        );
      }
    }

    sessionStorage.setItem(
      "selectedCar",
      JSON.stringify(car)
    );

    navigate("/cardetails");
  };

  // ============================================================
  // SHOW MORE / SHOW LESS
  // ============================================================

  const toggleExpanded = (carId) => {
    console.log(
      "🔵 SHOW MORE/LESS CLICKED:",
      carId
    );

    setExpandedCars((previous) => {
      const newState = {
        ...previous,
        [carId]: !previous[carId],
      };

      console.log(
        "🔵 EXPANDED STATE:",
        newState
      );

      return newState;
    });
  };

  // ============================================================
  // GET VALUE FROM FEATURE OBJECT
  // ============================================================

  const getFeatureValue = (item) => {
    if (
      item === undefined ||
      item === null
    ) {
      return "";
    }

    // String / number
    if (
      typeof item === "string" ||
      typeof item === "number"
    ) {
      return String(item).trim();
    }

    // Object
    if (typeof item === "object") {
      return String(
        item.item_name ||
          item.name ||
          item.mldynsec_val ||
          item.value ||
          item.title ||
          item.label ||
          item.description ||
          ""
      ).trim();
    }

    return "";
  };

  // ============================================================
  // GET FEATURES FROM API
  // ============================================================

  const getFeatures = (car) => {
    let source = [];

    // includedItems
    if (
      Array.isArray(car?.includedItems) &&
      car.includedItems.length > 0
    ) {
      source = car.includedItems;
    }

    // features
    else if (
      Array.isArray(car?.features) &&
      car.features.length > 0
    ) {
      source = car.features;
    }

    // included_items
    else if (
      Array.isArray(car?.included_items) &&
      car.included_items.length > 0
    ) {
      source = car.included_items;
    }

    // carFeatures
    else if (
      Array.isArray(car?.carFeatures) &&
      car.carFeatures.length > 0
    ) {
      source = car.carFeatures;
    }

    // car_features
    else if (
      Array.isArray(car?.car_features) &&
      car.car_features.length > 0
    ) {
      source = car.car_features;
    }

    return source
      .map(getFeatureValue)
      .filter(Boolean);
  };

  // ============================================================
  // CLOSE DETAILS MODAL
  // ============================================================

  const closeDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedCar(null);
    setDetailsError("");
  };

  // ============================================================
  // CAR DETAILS
  // ============================================================

  const handleCarDetails = async (car) => {
    const carId =
      car.carmodel_id ||
      car.car_id ||
      car.id;

    if (!carId) {
      console.error(
        "Car ID not found:",
        car
      );
      return;
    }

    setShowDetailsModal(true);
    setDetailsLoading(true);
    setSelectedCar(null);
    setDetailsError("");

    try {
      const response = await api.get(
        `/cars/${carId}/details?langid=${languageId}`
      );

      console.log(
        "🚗 CAR DETAILS RESPONSE:",
        response.data
      );

      if (response.data?.success) {
        setSelectedCar(
          response.data.data
        );
      } else {
        setDetailsError(
          response.data?.message ||
            tt(
              "carDetailsNotAvailable",
              "Car details not available."
            )
        );
      }
    } catch (error) {
      console.error(
        "CAR DETAILS ERROR:",
        error
      );

      setDetailsError(
        error.response?.data?.message ||
          tt(
            "unableToLoadCarDetails",
            "Unable to load car details."
          )
      );
    } finally {
      setDetailsLoading(false);
    }
  };

  // ============================================================
  // LOADING
  // ============================================================

  if (loading) {
    return (
      <div
        style={{
          minHeight: "60vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div
          className="spinner-border"
          role="status"
        >
          <span className="visually-hidden">
            {tt(
              "loading",
              "Loading..."
            )}
          </span>
        </div>
      </div>
    );
  }

  // ============================================================
  // PAGE
  // ============================================================

  return (
    <>
      <div className="p20">
        <div className="p0 container container2">
          <div
            className="col-md-12 main-content"
            style={{ float: "left" }}
          >
            {/* ==================================================
                NO CARS
            ================================================== */}

            {cars.length === 0 ? (
              <div className="text-center py-5">
                <h4>
                  {tt(
                    "noCarsAvailable",
                    "No cars available."
                  )}
                </h4>
              </div>
            ) : (
              /* ==================================================
                 CAR LIST
              ================================================== */

              cars.map((car, index) => {
                // ==================================================
                // CAR ID
                // ==================================================

                const carId =
                  car.carmodel_id ||
                  car.car_id ||
                  car.id ||
                  `car-${index}`;

                // ==================================================
                // BASIC CAR DATA
                // ==================================================

                const image = getCarImage(
                  car.image ||
                    car.carmodel_image ||
                    car.car_image
                );

                const carName =
                  car.name ||
                  car.car_name ||
                  car.carmodel_name ||
                  tt(
                    "car",
                    "Car"
                  );

                const category =
                  car.category ||
                  car.car_category ||
                  car.carmodel_category ||
                  "";

                const price =
                  car.pricePerDay ??
                  car.price_per_day ??
                  car.price ??
                  0;

                const passengers =
                  car.passengers ??
                  car.persons ??
                  car.carmodel_passgrno ??
                  0;

                const luggage =
                  car.luggage ??
                  car.bags ??
                  car.carmodel_luggno ??
                  0;

                const doors =
                    car.doors ||
                    car.car_doors ||
                    car.carmodel_doors ||
                    0;

                const airco =
                  car.airco ??
                  car.air_condition ??
                  car.airConditioning ??
                  false;

                const transmission =
                  car.transmission ||
                  car.gearbox ||
                  car.carmodel_transmission ||
                  "";

                // ==================================================
                // FEATURES
                // ==================================================

                const features =
                  getFeatures(car);

                const mainFeatures =
                  features.slice(0, 4);

                const extraFeatures =
                  features.slice(4);

                // ==================================================
                // EXPANDED STATE
                // ==================================================

                const isExpanded =
                  expandedCars[carId] === true;

                console.log(
                  `🚗 ${carName} | features:`,
                  features.length,
                  "| extra:",
                  extraFeatures.length,
                  "| expanded:",
                  isExpanded
                );

                // ==================================================
                // CARD
                // ==================================================

                return (
                  <div
                    className="rental-card"
                    key={carId}
                  >
                    <div className="card-main-content">

                      {/* ==================================================
                          TITLE
                      ================================================== */}

                      <div
                        className="col-xs-12 col-sm-4 col-md-9 col-lg-9"
                        style={{
                          float: "left",
                        }}
                      >
                        <h2 className="car-title">
                          {carName}
                        </h2>

                        <div className="car-subtitle">
                          {category}
                        </div>
                      </div>

                      {/* ==================================================
                          PRICE
                      ================================================== */}

                      <div
                        className="col-xs-12 col-sm-4 col-md-3 col-lg-3"
                        style={{
                          float: "left",
                        }}
                      >
                        <div className="price-container">
                          <div className="price-label">
                            {tt(
                              "perDay",
                              "(Per day)"
                            )}
                          </div>

                          <div className="main-price">
                            {Number(
                              price
                            ).toFixed(2)}{" "}
                            €
                          </div>
                        </div>
                      </div>

                      <div className="clearfix"></div>

                      {/* ==================================================
                          IMAGE
                      ================================================== */}

                      <div
                        className="col-xs-12 col-sm-4 col-md-4 col-lg-64"
                        style={{
                          float: "left",
                        }}
                      >
                        <div className="image-container">
                          <img
                            src={image}
                            alt={carName}
                            className="car-img"
                            onError={
                              handleImageError
                            }
                          />
                        </div>
                      </div>

                      {/* ==================================================
                          FEATURES
                      ================================================== */}

                      <div
                        className="col-xs-12 col-sm-4 col-md-4 col-lg-64"
                        style={{
                          float: "left",
                        }}
                      >
                        <div className="features-container">

                          {/* ==================================================
                              BASIC SPECIFICATIONS
                          ================================================== */}

                          <div className="specs-row">
                            <span className="spec-tag">
                              👤 {passengers}
                            </span>

                            <span className="spec-tag">
                              🧳 {luggage}
                            </span>

                            <span className="spec-tag">
                              ❄️{" "}
                              {airco
                                ? tt(
                                    "airCo",
                                    "Air-Co"
                                  )
                                : tt(
                                    "noAirCo",
                                    "No Air-Co"
                                  )}
                            </span>

                            <span className="spec-tag">
                              🚪 {doors || "-"}
                           </span>
                           
                          </div>

                          {/* ==================================================
                              FIRST 4 FEATURES
                          ================================================== */}

                          {mainFeatures.length >
                          0 ? (
                            mainFeatures.map(
                              (
                                feature,
                                featureIndex
                              ) => (
                                <div
                                  className="feature-item"
                                  key={`main-${carId}-${featureIndex}`}
                                >
                                  <img
                                        src={tickImage}
                                        alt="Included"
                                        className="feature-icon"
                                        style={{
                                          width: "20px",
                                          height: "20px",
                                          minWidth: "20px",
                                          objectFit: "contain",
                                          marginRight: "8px",
                                          display: "inline-block",
                                          verticalAlign: "middle",
                                        }}
                                      />

                                  <strong>
                                    {feature}
                                  </strong>
                                </div>
                              )
                            )
                          ) : (
                            <>
                              <div className="feature-item">
                                <span className="feature-icon">
                                  ✓
                                </span>

                                <strong>
                                  {tt(
                                    "noExtraCost",
                                    "No Extra Cost"
                                  )}
                                </strong>
                              </div>

                              <div className="feature-item">
                                <span className="feature-icon">
                                  ✓
                                </span>

                                <strong>
                                  {tt(
                                    "allInclusive",
                                    "All inclusive"
                                  )}
                                </strong>
                              </div>

                              <div className="feature-item">
                                <span className="feature-icon">
                                  ✓
                                </span>

                                <strong>
                                  {tt(
                                    "noGuarantee",
                                    "No guarantee"
                                  )}
                                </strong>
                              </div>

                              <div className="feature-item">
                                <span className="feature-icon">
                                  ✓
                                </span>

                                <strong>
                                  {tt(
                                    "noCreditCard",
                                    "No credit card required"
                                  )}
                                </strong>
                              </div>
                            </>
                          )}

                          {/* ==================================================
                              SHOW MORE / SHOW LESS BUTTON

                              IMPORTANT:
                              No .modify class here.
                              No .test1 class here.
                              No .test-box1 class here.

                              This avoids CSS interference.
                          ================================================== */}

                          {extraFeatures.length > 0 && (
                            <div
                              style={{
                                width: "100%",
                                display: "block",
                                clear: "both",
                                marginTop: "8px",
                                marginBottom: "8px",
                                position: "relative",
                                zIndex: 9999,
                              }}
                            >
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();

                                  toggleExpanded(
                                    carId
                                  );
                                }}
                                style={{
                                  border: "0",
                                  outline: "none",
                                  background:
                                    "transparent",
                                  color:
                                    "#087cff",
                                  cursor:
                                    "pointer",
                                  padding:
                                    "6px 0",
                                  margin: 0,
                                  fontSize:
                                    "14px",
                                  fontWeight:
                                    "600",
                                  lineHeight:
                                    "1.4",
                                  position:
                                    "relative",
                                  zIndex: 10000,
                                  pointerEvents:
                                    "auto",
                                }}
                              >
                                {isExpanded
                                  ? tt(
                                      "showLess",
                                      "Show Less"
                                    )
                                  : `${tt(
                                      "show",
                                      "Show"
                                    )} ${
                                      extraFeatures.length
                                    } ${tt(
                                      "more",
                                      "More"
                                    )}`}
                              </button>
                            </div>
                          )}

                          {/* ==================================================
                              EXTRA FEATURES

                              This is rendered ONLY when expanded.
                          ================================================== */}

                          {isExpanded &&
                            extraFeatures.length >
                              0 && (
                              <div
                                style={{
                                  width:
                                    "100%",
                                  display:
                                    "block",
                                  clear:
                                    "both",
                                  marginTop:
                                    "5px",
                                  position:
                                    "relative",
                                  zIndex: 10,
                                }}
                              >
                                {extraFeatures.map(
                                  (
                                    feature,
                                    featureIndex
                                  ) => (
                                    <div
                                      className="feature-item"
                                      key={`extra-${carId}-${featureIndex}`}
                                    >
                                       <img
                                        src={tickImage}
                                        alt="Included"
                                        className="feature-icon"
                                        style={{
                                          width: "20px",
                                          height: "20px",
                                          minWidth: "20px",
                                          objectFit: "contain",
                                          marginRight: "8px",
                                          display: "inline-block",
                                          verticalAlign: "middle",
                                        }}
                                      />

                                      <strong>
                                        {feature}
                                      </strong>
                                    </div>
                                  )
                                )}
                              </div>
                            )}
                        </div>
                      </div>

                      {/* ==================================================
                          ACTIONS
                      ================================================== */}

                      <div
                        className="col-xs-12 col-sm-4 col-md-4 col-lg-64"
                        style={{
                          float: "left",
                        }}
                      >
                        <div className="actions-container">

                          {/* CAR DETAILS */}

                          <div className="car-d">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();

                                handleCarDetails(
                                  car
                                );
                              }}
                              style={{
                                border: "none",
								cursor:
                                  "pointer",
                                position:
                                  "relative",
                                zIndex: 5,
                              }}
                            >
                              {tt(
                                "carDetails",
                                "Car details"
                              )}
                            </button>
                          </div>

                          {/* BOOK THIS */}

                          <button
                            type="button"
                            className="book-btn"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();

                              handleBookCar(
                                car
                              );
                            }}
                            style={{
                              position:
                                "relative",
                              zIndex: 5,
                            }}
                          >
                            {tt(
                              "bookThis",
                              "BOOK THIS"
                            )}
                          </button>
                        </div>
                      </div>

                      <div className="clearfix"></div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* ============================================================
          CAR DETAILS MODAL
      ============================================================ */}

     {/* ============================================================
    PROFESSIONAL CAR DETAILS MODAL
============================================================ */}
{showDetailsModal && (
  <div
    className="professional-car-overlay"
    onClick={closeDetailsModal}
  >
    <div
      className="professional-car-modal"
      onClick={(e) => e.stopPropagation()}
    >

      {/* ========================================================
          HEADER
      ======================================================== */}
      <div className="professional-car-header">

        <div className="professional-car-header-text">

          <div className="professional-car-eyebrow">
            {tt(
              "vehicleInformation",
              "VEHICLE INFORMATION"
            )}
          </div>

          <h2 className="professional-car-title">
            {selectedCar?.name ||
              selectedCar?.car_name ||
              selectedCar?.carmodel_name ||
              tt("carDetails", "Car Details")}
         

          <div className="professional-car-subtitle">
            {selectedCar?.category ||
              selectedCar?.car_category ||
              selectedCar?.group_name ||
              ""}
          </div>
 </h2>
        </div>

        <button
          type="button"
          className="professional-car-close"
          onClick={closeDetailsModal}
          aria-label="Close"
        >
          ×
        </button>

      </div>


      {/* ========================================================
          SCROLLABLE BODY
      ======================================================== */}
      <div className="professional-car-body">

        {/* ======================================================
            LOADING
        ====================================================== */}
        {detailsLoading && (
          <div className="professional-car-loading">

            <div className="professional-spinner"></div>

            <div>
              {tt(
                "loadingCarDetails",
                "Loading car details..."
              )}
            </div>

          </div>
        )}


        {/* ======================================================
            ERROR
        ====================================================== */}
        {!detailsLoading && detailsError && (
          <div className="professional-car-error">
            {detailsError}
          </div>
        )}


        {/* ======================================================
            DETAILS
        ====================================================== */}
        {!detailsLoading &&
          selectedCar &&
          !detailsError && (
            <>

              {/* ==================================================
                  TOP INFORMATION
              ================================================== */}
              <div className="professional-car-top">

                {/* =================================================
                    IMAGE
                ================================================= */}
                <div className="professional-car-image-section">

                  <div className="professional-car-image-box">

                    <img
                      src={getCarImage(
                        selectedCar.image ||
                          selectedCar.carmodel_image ||
                          selectedCar.car_image
                      )}
                      alt={
                        selectedCar.name ||
                        selectedCar.model ||
                        "Car"
                      }
                      onError={handleImageError}
                      className="professional-car-image"
                    />

                  </div>
  <div className="professional-description">

                  <div className="professional-description-title">
                    {tt(
                      "description",
                      "Description"
                    )}
                  </div>

                  <div className="professional-description-text">
                    {selectedCar.description ||
                      selectedCar.details ||
                      selectedCar.content}
                  </div>

                </div>
                </div>


                {/* =================================================
                    SPECIFICATIONS
                ================================================= */}
                <div className="professional-car-spec-section">

                  <div className="professional-section-title">

                    <span className="professional-section-icon">
                      🚗
                    </span>

                    <span>
                      {tt(
                        "vehicleSpecifications",
                        "Vehicle Specifications"
                      )}
                    </span>

                  </div>


                  <div className="professional-spec-grid">

                    {/* PASSENGERS */}
                    <div className="professional-spec-card">

                      <div className="professional-spec-icon">
                        👤
                      </div>

                      <div>
                        <div className="professional-spec-label">
                          {tt(
                            "passengers",
                            "Passengers"
                          )}
                        </div>

                        <div className="professional-spec-value">
                          {selectedCar.passengers ??
                            selectedCar.persons ??
                            selectedCar.carmodel_passgrno ??
                            "-"}
                        </div>
                      </div>

                    </div>


                    {/* LUGGAGE */}
                    <div className="professional-spec-card">

                      <div className="professional-spec-icon">
                        🧳
                      </div>

                      <div>
                        <div className="professional-spec-label">
                          {tt(
                            "luggage",
                            "Luggage"
                          )}
                        </div>

                        <div className="professional-spec-value">
                          {selectedCar.luggage ??
                            selectedCar.bags ??
                            selectedCar.carmodel_luggno ??
                            "-"}
                        </div>
                      </div>

                    </div>


                    {/* TRANSMISSION */}
                    <div className="professional-spec-card">

                      <div className="professional-spec-icon">
                        ⚙️
                      </div>

                      <div>
                        <div className="professional-spec-label">
                          {tt(
                            "transmission",
                            "Transmission"
                          )}
                        </div>

                        <div className="professional-spec-value">
                          {selectedCar.transmission ||
                            selectedCar.gearbox ||
                            selectedCar.carmodel_transmission ||
                            "-"}
                        </div>
                      </div>

                    </div>


                    {/* AIR CONDITIONING */}
                    <div className="professional-spec-card">

                      <div className="professional-spec-icon">
                        ❄️
                      </div>

                      <div>
                        <div className="professional-spec-label">
                          {tt(
                            "airConditioning",
                            "Air Conditioning"
                          )}
                        </div>

                        <div className="professional-spec-value">

                          {(
                            selectedCar.airConditioning ??
                            selectedCar.air_conditioning ??
                            selectedCar.airco ??
                            selectedCar.air_condition
                          )
                            ? tt("yes", "Yes")
                            : tt("no", "No")}

                        </div>

                      </div>

                    </div>


                    {/* DOORS */}
                    <div className="professional-spec-card">

                      <div className="professional-spec-icon">
                        🚪
                      </div>

                      <div>
                        <div className="professional-spec-label">
                          {tt(
                            "doors",
                            "Doors"
                          )}
                        </div>

                        <div className="professional-spec-value">
                          {selectedCar.doors || "-"}
                        </div>
                      </div>

                    </div>


                    {/* ENGINE */}
                    <div className="professional-spec-card">

                      <div className="professional-spec-icon">
                        🔧
                      </div>

                      <div>
                        <div className="professional-spec-label">
                          {tt(
                            "engineCapacity",
                            "Engine Capacity"
                          )}
                        </div>

                        <div className="professional-spec-value">
                          {selectedCar.engineCapacity ??
                            selectedCar.engine_capacity ??
                            "-"}
                        </div>
                      </div>

                    </div>


                    {/* CENTRAL LOCKING */}
                    <div className="professional-spec-card">

                      <div className="professional-spec-icon">
                        🔒
                      </div>

                      <div>
                        <div className="professional-spec-label">
                          {tt(
                            "centralLocking",
                            "Central Locking"
                          )}
                        </div>

                        <div className="professional-spec-value">

                          {(
                            selectedCar.centralLocking ??
                            selectedCar.central_locking
                          )
                            ? tt("yes", "Yes")
                            : tt("no", "No")}

                        </div>

                      </div>

                    </div>


                    {/* AIRBAG */}
                    <div className="professional-spec-card">

                      <div className="professional-spec-icon">
                        🛡️
                      </div>

                      <div>
                        <div className="professional-spec-label">
                          {tt(
                            "airbag",
                            "Airbag"
                          )}
                        </div>

                        <div className="professional-spec-value">

                          {(
                            selectedCar.airbag
                          )
                            ? tt("yes", "Yes")
                            : tt("no", "No")}

                        </div>

                      </div>

                    </div>

                  </div>
 {/* ==================================================
                  INCLUDED FEATURES
              ================================================== */}
              <div className="professional-included-section">

                <div className="professional-section-title">

                  <span className="professional-section-icon included-icon">
                    ✓
                  </span>

                  <span>
                    {tt(
                      "includedFeatures",
                      "Included Features"
                    )}
                  </span>

                </div>


                <div className="professional-feature-grid">

                  {(() => {

                    const detailFeatures =
                      getFeatures(selectedCar);

                    /*
                     * If API has features, use them.
                     * Otherwise show your standard
                     * included features.
                     */

                    const defaultFeatures = [
                      tt(
                        "noExtraCost",
                        "No Extra Cost"
                      ),

                      tt(
                        "allInclusive",
                        "All inclusive"
                      ),

                      tt(
                        "noGuarantee",
                        "No guarantee"
                      ),

                      tt(
                        "noCreditCard",
                        "No credit card required"
                      ),

                      tt(
                        "freeDelivery",
                        "Free delivery all over Crete"
                      ),

                      tt(
                        "freeRoadmap",
                        "Free roadmap of Crete"
                      ),

                      tt(
                        "unlimitedMileage",
                        "Unlimited mileage"
                      ),

                      tt(
                        "roadAssistance",
                        "24/7 road assistance"
                      ),

                      tt(
                        "fullInsurance",
                        "Full insurance!!"
                      )
                    ];

                    const features =
                      detailFeatures.length > 0
                        ? detailFeatures
                        : defaultFeatures;

                    return features.map(
                      (feature, index) => (

                        <div
                          className="professional-feature-card"
                          key={`detail-feature-${index}`}
                        >

                          <span className="professional-feature-check">
                            ✓
                          </span>

                          <span className="professional-feature-text">
                            {feature}
                          </span>

                        </div>

                      )
                    );

                  })()}

                </div>

              </div>

                </div>

              </div>


             

              {/* ==================================================
                  DESCRIPTION
              ================================================== 
              {(selectedCar.description ||
                selectedCar.details ||
                selectedCar.content) && (

                <div className="professional-description">

                  <div className="professional-description-title">
                    {tt(
                      "description",
                      "Description"
                    )}
                  </div>

                  <div className="professional-description-text">
                    {selectedCar.description ||
                      selectedCar.details ||
                      selectedCar.content}
                  </div>

                </div>

              )}*/}

            </>
          )}


        {/* ======================================================
            NO DETAILS
        ====================================================== */}
        {!detailsLoading &&
          !selectedCar &&
          !detailsError && (

            <div className="professional-no-details">

              {tt(
                "noCarDetailsFound",
                "No car details found."
              )}

            </div>

          )}

      </div>


      {/* ========================================================
          FOOTER
      ======================================================== */}
      {!detailsLoading &&
        selectedCar &&
        !detailsError && (

          <div className="professional-car-footer">

            <div className="professional-footer-note">

              <span className="professional-footer-icon">
                🔐
              </span>

              <span>
                {tt(
                  "vehicleNote",
                  "Vehicle specifications may vary slightly depending on the actual vehicle provided."
                )}
              </span>

            </div>


            <div className="professional-footer-actions">

              <button
                type="button"
                className="button1 professional-close-button"
                onClick={closeDetailsModal}
              >
                {tt(
                  "close",
                  "Close"
                )}
              </button>

              <button
                type="button"
                className="button2 professional-book-button"
                onClick={() => {
                  closeDetailsModal();
                  handleBookCar(selectedCar);
                }}
              >
                {tt(
                  "bookThis",
                  "BOOK THIS"
                )}
              </button>

            </div>

          </div>

        )}

    </div>
  </div>
)}
    </>
  );
};

// ============================================================
// CAR DETAIL ROW
// ============================================================

function CarDetailRow({
  label,
  value,
}) {
  return (
    <div className="car-detail-row">
      <div className="car-detail-label">
        {label} :
      </div>

      <div className="car-detail-value">
        {value !== undefined &&
        value !== null &&
        value !== "" ? (
          typeof value === "object" ? (
            "-"
          ) : (
            value
          )
        ) : (
          "-"
        )}
      </div>
    </div>
  );
}

export default OurCars;